<template>
	<form class="form-channel-2">
		<div class="card-data">
			<!-- Dialog for creating a new circle -->
			<create-circle :visible.sync="addingCircle" :community-id="community_id" @save="onAddCircle"
				@cancel="addingCircle = false" />
			<md-dialog
				:md-active.sync="unsavedCircleDialogActive"
				:md-click-outside-to-close="false"
				:md-close-on-esc="false">

				<md-dialog-title>Unsaved circle changes</md-dialog-title>

				<md-dialog-content>
					You have unsaved changes. Do you want to save or discard them before switching circles?
				</md-dialog-content>

				<md-dialog-actions>
					<md-button @click="discardPendingCircleChanges">Discard</md-button>
					<md-button class="md-primary" @click="savePendingCircleChanges">Save</md-button>
				</md-dialog-actions>
			</md-dialog>
			<md-list :md-expand-single="expandSingle">
				<md-list-item>
					<md-button class="picker-icon picker-name" @click="addingCircle = true">
						<font-awesome-icon class='small-icon picker-icon' icon="plus-circle" aria-hidden="true" />
						<span class="add-circle">Add Circle</span>
					</md-button>
				</md-list-item>
				<md-list-item v-for="(circle, idx) in additionalParams" v-bind:key="circle.short"
					:ref="`circleItem-${circle.short}`" md-expand :md-expanded.sync="circle.expanded"
					@update:mdExpanded="expanded => onCircleExpanded(idx, expanded)"
					:class="{ 'closed-circle': circle.closed }">
					<span class="md-list-item-text community-expand-button">
						{{ circle.name }}
						<i v-if="circle.closed" class="fa fa-lock circle-status-icon" aria-hidden="true"
							title="Closed circle"></i>
					</span>
					<md-list v-show="circle.expanded" slot="md-expand" class="md-layout md-gutter md-field pt-0 mb-0 mlminus-15 col-md-12">
						<div class="button-row pt-10 pb-10">
							<delete-circle-confirm :circle="circle ? circle : ''" :community="community_id" value="5"
								@reload-data="loadData">
							</delete-circle-confirm>
						</div>
						<md-divider></md-divider>
						<div class="md-table-cell-container wizard-buttons m-left-5">
							<template v-if="circle.editing">
								<span @click="saveCircle(idx)">
									<i class="fas fa-save small-icon picker-icon"></i>
									<md-tooltip md-direction="left">Save</md-tooltip>
								</span>
								<span @click="cancelEdit(idx)" class="m-left-10">
									<i class="fas fa-times picker-icon-danger"></i>
									<md-tooltip md-direction="right">Cancel</md-tooltip>
								</span>
							</template>
							<template v-else>
								<span @click="startEdit(idx)">
									<i class="fa fa-edit small-icon"></i>
									<md-tooltip md-direction="left">Edit</md-tooltip>
								</span>
							</template>
						</div>
						<div class="button-row pt-10 pb-10">
							<md-button class="picker-icon picker-name" @click="toggleCircle(idx)">
								<i class="fa fa-times-circle" aria-hidden="true"></i>
								{{ additionalParams[idx].closed ? 'Open circle' : 'Close circle' }}
							</md-button>
						</div>
						<md-list-item>
							<div class="circle-detail-row md-layout-item md-layout md-gutter">
								<circle-tabs v-if="circle.expanded"	:key="circle.short" :actionPageOptions="actionPageOptions"
									:circle="circle" :community="community_id" :idx="idx"
									:limits="CIRCLE_LIMITS"></circle-tabs>
							</div>
						</md-list-item>
					</md-list>
				</md-list-item>
			</md-list>
		</div>
	</form>
</template>
<script>
import API from "../services/API";
import DeleteCircleConfirm from "./DeleteCircleConfirm.vue";
import CreateCircle from "./CreateCircle.vue";
import CircleTabs from "./CircleTabs.vue";
export default {
	components: {
		DeleteCircleConfirm,
		CreateCircle,
		CircleTabs
	},
	props: {
		community_id: {
			type: [String, Number],
			required: true
		}
	},
	data() {
		return {
			api: new API(),
			additionalParams: [],
			addingCircle: false,
			expandSingle: true,
			actionPageOptions: [],
			// Defining min/max for each type
			CIRCLE_LIMITS: {
				subjects: { min: 10, max: 100 },
				intros: { min: 100, max: 500 },
				questions: { min: 250, max: 2000 },
				thankyou: { min: 5, max: 500 },
				prompts: { min: 200, max: 500 }
			},
			pendingCircleIdx: null,
			unsavedCircleDialogActive: false,
			isHandlingCircleSwitch: false

		}
	},
	watch: {
		$route: "loadData"
	},
	created() {
		this.loadData();
	},
	methods: {
		async loadCustomizations(circle) {
			const { data } = await this.api.get(`configuration/circle-values/batch?community=${encodeURIComponent(this.community_id)}&circle=${encodeURIComponent(circle.short)}`);
			Object.assign(circle, {
				interests: data.interests,
				subjects: data['wizard-headlines'],
				intros: data['wizard-intros'],
				questions: data['wizard-contents'],
				thankyou: data['wizard-closes'],
				prompts: data['wizard-prompts']
			});
		},
		async loadData() {
			await this.getActionPage();
			await this.api.get(`communities/${this.community_id}`).then(async community => {
				if (community.data) {
					this.community = community.data;

					this.additionalParams = (community.data.additionalParams || [])
						.map(c => ({
							...c,
							interests: [],
							subjects: [],
							intros: [],
							questions: [],
							thankyou: [],
							prompts: [],
							editing: false,
							expanded: false
						}))
						.sort((a, b) => {
							// false (open) comes before true (closed)
							return a.closed - b.closed;
						});
				}
			});
		},
		async getActionPage() {
			await this.api.get(`action/get-action-pages`).then(async actionPage => {
				this.actionPageOptions = actionPage.data;
			}).catch(error => {
				console.error('Error fetching action pages:', error);
			})
		},
		getCircleChangeKeys() {
			return [
				'name',
				'closed',
				'actionPage',
				'administrators',
				'circlePageUrl',
				'interests',
				'subjects',
				'intros',
				'questions',
				'thankyou',
				'prompts'
			];
		},

		getCircleChanges(idx) {
			const circle = this.additionalParams[idx];
			const before = circle._original || {};
			const changes = {};

			this.getCircleChangeKeys().forEach(key => {
				if (JSON.stringify(circle[key]) !== JSON.stringify(before[key])) {
					changes[key] = circle[key];
				}
			});

			return changes;
		},

		hasCircleChanges(idx) {
			return Object.keys(this.getCircleChanges(idx)).length > 0;
		},

		getEditingCircleIdx(excludeIdx = null) {
			return this.additionalParams.findIndex((circle, idx) => {
				return circle.editing && idx !== excludeIdx;
			});
		},

		async onCircleExpanded(idx, expanded) {
			if (this.isHandlingCircleSwitch) {
				return;
			}

			if (!expanded) {
				this.additionalParams[idx].expanded = false;
				return;
			}

			const editingIdx = this.getEditingCircleIdx(idx);

			if (editingIdx !== -1) {
				if (!this.hasCircleChanges(editingIdx)) {
					this.cancelEdit(editingIdx);
					await this.expandCircle(idx);
					return;
				}

				this.pendingCircleIdx = idx;
				this.unsavedCircleDialogActive = true;

				this.isHandlingCircleSwitch = true;
				this.additionalParams[idx].expanded = false;
				await this.$nextTick();
				this.isHandlingCircleSwitch = false;

				return;
			}

			await this.expandCircle(idx);
		},

		async expandCircle(idx) {
			this.isHandlingCircleSwitch = true;

			try {
				await this.loadCustomizations(this.additionalParams[idx]);

				this.additionalParams.forEach((circle, circleIdx) => {
					circle.expanded = circleIdx === idx;
				});

				await this.$nextTick();
			} finally {
				this.isHandlingCircleSwitch = false;
			}
		},
		clearPendingCircleSwitch() {
			this.pendingCircleIdx = null;
			this.unsavedCircleDialogActive = false;
		},

		async discardPendingCircleChanges() {
			const pendingIdx = this.pendingCircleIdx;
			const editingIdx = this.getEditingCircleIdx(pendingIdx);

			if (editingIdx !== -1) {
				this.cancelEdit(editingIdx);
			}

			this.clearPendingCircleSwitch();

			if (pendingIdx !== null) {
				await this.expandCircle(pendingIdx);
			}
		},

		async savePendingCircleChanges() {
			const pendingIdx = this.pendingCircleIdx;
			const editingIdx = this.getEditingCircleIdx(pendingIdx);

			if (editingIdx !== -1) {
				const saved = await this.saveCircle(editingIdx);

				if (!saved) {
					return;
				}
			}

			this.clearPendingCircleSwitch();

			if (pendingIdx !== null) {
				await this.expandCircle(pendingIdx);
			}
		},
		async onAddCircle(circle) {
			this.addingCircle = false;
			try {
				await this.api.put(
					`communities/process-circle/${this.community_id}`,
					{ action: 'add', circle: circle }
				).then(response => {
					circle.editing = false;
					this.$toast.success('Circle created!');
				});
			} catch (err) {
				console.error('Error creating circle:', err);
				this.$toast.error('Error creating circle');
			} finally {
				await this.loadData();

				// Wait until DOM updates, then focus its input
				this.$nextTick(() => {
					const listItem = this.$refs[`circleItem-${circle.short}`];
					const el = Array.isArray(listItem)
						? (listItem[0] && listItem[0].$el)
						: (listItem && listItem.$el);
					// Ensure we handle both single and multiple refs correctly
					if (el) {
						el.scrollIntoView({ behavior: 'smooth', block: 'start' })
					}

					const mdInput = this.$refs[`nameInput-${circle.short}`];
					const comp = Array.isArray(mdInput) ? mdInput[0] : mdInput;
					if (comp && comp.$el) {
						const input = comp.$el.querySelector('input');
						input && input.focus();
					}
				});
			}
		},
		toggleCircle(idx) {
			const circle = this.additionalParams[idx];

			if (!circle._original) {
				circle._original = JSON.parse(JSON.stringify(circle));
			}

			circle.closed = !circle.closed;
			circle.editing = true;
		},
		startEdit(idx) {
			const circle = this.additionalParams[idx]
			circle._original = JSON.parse(JSON.stringify(circle))
			circle.editing = true;
		},
		cancelEdit(idx) {
			const circle = this.additionalParams[idx];
			const currentExpanded = circle.expanded;

			if (circle._original) {
				Object.assign(circle, circle._original);
				delete circle._original;
			}

			circle.expanded = currentExpanded;
			circle.editing = false;
		},
		async saveCircle(idx) {
			const circle = this.additionalParams[idx]
			const changes = this.getCircleChanges(idx);

			if (Object.keys(changes).length === 0) {
				circle.editing = false;
				delete circle._original;
				return true;
			}

			// Validate min/max before API call
			for (const [field, { min, max }] of Object.entries(this.CIRCLE_LIMITS)) {
				if (changes[field]) {
				// Handle both string and array cases
				const values = Array.isArray(changes[field]) ? changes[field] : [changes[field]];
				for (const val of values) {
					const length = typeof val === 'string' ? val.trim().length : 0;
					if (length < min) {
					this.$toast.error(`${field} must be at least ${min} characters.`);
					return false;
					}
					if (length > max) {
					this.$toast.error(`${field} must be no more than ${max} characters.`);
					return false;
					}
				}
				}
			}

			// always including the short code so the backend can find which circle to patch
			changes.short = circle.short
			try {

				await this.api.put(
					`communities/process-circle/${this.community_id}`,
					{ action: 'update', circle: changes }
				);

				await this.loadCustomizations(circle);
				this.$toast.success(`Circle updated successfully!`);
				circle.editing = false;
				delete circle._original;
				return true;


			} catch (err) {
				console.error('Error saving circle:', err);
				this.$toast.error('Error saving circle');
				return false;
			} finally {
				// clearing flags
				circle.editing = false;
			}
		}
	}
}
</script>
<style>
.button-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.md-layout {
	display: flex;
	flex-wrap: nowrap;
}

.md-list-item-text.community-expand-button {
	display: inline-block;
	vertical-align: middle;
}

.circle-status-icon {
	display: inline-block;
	margin-left: 6px;
	color: #888;
	vertical-align: middle;
}

</style>


// WEBPACK FOOTER //
// src/components/Circles.vue